/**
 * Smart Notification Manager
 * Generates contextual reminders and notifications based on user activity
 * Limited to max 3 notifications to avoid overwhelming users
 */

// Check if a notification type was dismissed recently (within 7 days)
function isNotificationDismissed(notificationType) {
  try {
    const dismissed = JSON.parse(localStorage.getItem('dismissedNotifications') || '{}');
    const dismissedAt = dismissed[notificationType];
    if (!dismissedAt) return false;
    const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSinceDismissed < 7; // Don't show again for 7 days
  } catch {
    return false;
  }
}

// Check if a notification type was recently read (within specified hours, default 24)
function wasRecentlyRead(notificationType, hoursToHide = 24) {
  try {
    const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '{}');
    const readAt = readNotifications[notificationType];
    if (!readAt) return false;
    const hoursSinceRead = (Date.now() - readAt) / (1000 * 60 * 60);
    return hoursSinceRead < hoursToHide;
  } catch {
    return false;
  }
}

// Check if a notification was recently shown/generated (regardless of user interaction)
function wasRecentlyShown(key, hoursToHide = 168) {
  try {
    const shownTimestamps = JSON.parse(localStorage.getItem('shownNotifications') || '{}');
    const shownAt = shownTimestamps[key];
    if (!shownAt) return false;
    const hoursSinceShown = (Date.now() - shownAt) / (1000 * 60 * 60);
    return hoursSinceShown < hoursToHide;
  } catch {
    return false;
  }
}

// Mark a notification as shown (prevents re-showing for the specified period)
function markShown(key) {
  try {
    const shownTimestamps = JSON.parse(localStorage.getItem('shownNotifications') || '{}');
    shownTimestamps[key] = Date.now();
    localStorage.setItem('shownNotifications', JSON.stringify(shownTimestamps));
  } catch {}
}

// Dismiss a notification type for 7 days
export const dismissNotificationType = (notificationType) => {
  try {
    const dismissed = JSON.parse(localStorage.getItem('dismissedNotifications') || '{}');
    dismissed[notificationType] = Date.now();
    localStorage.setItem('dismissedNotifications', JSON.stringify(dismissed));
  } catch (error) {
    console.error('Failed to dismiss notification:', error);
  }
};

// Mark a notification type as read (hide for 24 hours)
export const markNotificationAsRead = (notificationType) => {
  try {
    const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '{}');
    readNotifications[notificationType] = Date.now();
    localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
  }
};

export const generateSmartNotifications = () => {
  const notifications = [];
  let notificationId = 1;

  try {
    // Check weight tracking reminder (only at 14+ days, once per cycle)
    const lastWeighIn = getLastWeighInDate();
    if (lastWeighIn) {
      const daysSinceWeighIn = Math.floor((Date.now() - lastWeighIn) / (1000 * 60 * 60 * 24));
      
      // Only show if 14+ days have passed AND we haven't notified about this specific weigh-in yet
      if (daysSinceWeighIn >= 14) {
        const lastNotifiedWeighIn = localStorage.getItem('lastNotifiedWeighIn');
        const alreadyNotifiedForThisCycle = lastNotifiedWeighIn && parseInt(lastNotifiedWeighIn) === lastWeighIn;
        
        if (!alreadyNotifiedForThisCycle) {
          // Mark this weigh-in as notified so we don't show again until next weight log
          localStorage.setItem('lastNotifiedWeighIn', lastWeighIn.toString());
          
          notifications.push({
            id: notificationId++,
            type: 'weight',
            title: 'Time for your fortnightly weigh-in! ⚖️',
            message: `It's been ${daysSinceWeighIn} days since your last weigh-in.`,
            time: 'Now',
            read: false,
            action: { text: 'Log Weight', link: '/my-goals?tab=weight' },
            priority: 'high'
          });
        }
      }
    }

    // Check monthly reflection reminder (only if they've done it before)
    const lastReflectionDate = getLastReflectionDate();
    if (lastReflectionDate && !isNotificationDismissed('reflection') && !wasRecentlyRead('reflection')) {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const lastReflectionMonth = new Date(lastReflectionDate).getMonth();
      const lastReflectionYear = new Date(lastReflectionDate).getFullYear();
      
      // Only remind after the 15th of the month
      const dayOfMonth = new Date().getDate();
      if ((lastReflectionMonth !== currentMonth || lastReflectionYear !== currentYear) && dayOfMonth >= 15) {
        const monthName = new Date().toLocaleDateString('en-US', { month: 'long' });
        notifications.push({
          id: notificationId++,
          type: 'reflection',
          title: `${monthName} reflection prompts are ready! 📝`,
          message: 'Take a moment to reflect on your wellness journey.',
          time: 'Today',
          read: false,
          action: { text: 'Start Reflection', link: '/my-goals?tab=reflection' },
          priority: 'medium'
        });
      }
    }

    // Removed random forum post notifications - too spammy

    // Check workout streak (only if at risk of losing 3+ day streak)
    const workoutStreak = getWorkoutStreak();
    const lastWorkout = getLastWorkoutDate();
    if (workoutStreak >= 3 && lastWorkout && !isNotificationDismissed('streak') && !wasRecentlyRead('streak')) {
      const daysSinceWorkout = Math.floor((Date.now() - lastWorkout) / (1000 * 60 * 60 * 24));
      // Only notify if they haven't worked out today (1+ days since last workout)
      if (daysSinceWorkout >= 1) {
        notifications.push({
          id: notificationId++,
          type: 'streak',
          title: `Keep your ${workoutStreak}-day streak going! 🔥`,
          message: daysSinceWorkout === 1 ? 'Complete a workout today!' : `${daysSinceWorkout} days since your last workout.`,
          time: 'Today',
          read: false,
          action: { text: 'Start Workout', link: '/workouts' },
          priority: 'high'
        });
      }
    }

    // Removed upcoming events - too noisy

    // Check meal plan reminder (weekly - only show once per 7 days)
    const mealPlan = getMealPlanStatus();
    if (mealPlan.daysSinceLastPlan >= 7 && !isNotificationDismissed('recipe') && !wasRecentlyShown('mealPlanShown', 168)) {
      markShown('mealPlanShown');
      notifications.push({
        id: notificationId++,
        type: 'recipe',
        title: 'Plan your meals for the week 🍽️',
        message: 'A meal plan helps you stay on track.',
        time: 'Today',
        read: false,
        action: { text: 'Browse Recipes', link: '/recipes' },
        priority: 'low'
      });
    }

    // Check goal progress (only at 90%+)
    const goalProgress = getGoalProgress();
    if (goalProgress.hasGoals && goalProgress.nearingMilestone && goalProgress.percentComplete >= 90 && !wasRecentlyRead('goal')) {
      notifications.push({
        id: notificationId++,
        type: 'goal',
        title: `Almost there! ${goalProgress.percentComplete}% complete 🎯`,
        message: goalProgress.message,
        time: 'Today',
        read: false,
        action: { text: 'View Goals', link: '/my-goals' },
        priority: 'medium'
      });
    }

    // Check for new program (published in last 7 days that user hasn't seen)
    const newProgram = getNewProgramAlert();
    if (newProgram.hasNew && !isNotificationDismissed('newProgram') && !wasRecentlyRead('newProgram')) {
      notifications.push({
        id: notificationId++,
        type: 'newProgram',
        title: `New Program: ${newProgram.programTitle} 🆕`,
        message: 'A fresh workout program is ready for you!',
        time: 'New',
        read: false,
        action: { text: 'Check It Out', link: '/workouts' },
        priority: 'high'
      });
    }

    // Check for new admin forum posts (Kate posting)
    const adminPost = getAdminForumPost();
    if (adminPost.hasNewPost && !isNotificationDismissed('adminPost') && !wasRecentlyRead('adminPost')) {
      notifications.push({
        id: notificationId++,
        type: 'adminPost',
        title: `Kate shared in the forum 💬`,
        message: adminPost.preview,
        time: 'New',
        read: false,
        action: { text: 'Read Post', link: '/community' },
        priority: 'high'
      });
    }

    // Check for forum replies to user's posts
    const forumReplies = getForumReplies();
    if (forumReplies.hasReplies && !isNotificationDismissed('forumReply') && !wasRecentlyRead('forumReply')) {
      notifications.push({
        id: notificationId++,
        type: 'community',
        title: `${forumReplies.replyCount} new ${forumReplies.replyCount === 1 ? 'reply' : 'replies'} to your post 💬`,
        message: forumReplies.latestReply,
        time: 'New',
        read: false,
        action: { text: 'View Discussion', link: '/community' },
        priority: 'high'
      });
    }

    // Check workout milestones (10, 25, 50, 100)
    const milestone = getWorkoutMilestone();
    if (milestone.hitMilestone && !isNotificationDismissed(`milestone_${milestone.count}`) && !wasRecentlyRead(`milestone_${milestone.count}`)) {
      notifications.push({
        id: notificationId++,
        type: 'milestone',
        title: `${milestone.count} Workouts Completed! 🎉`,
        message: milestone.message,
        time: 'Now',
        read: false,
        action: { text: 'Celebrate', link: '/dashboard' },
        priority: 'high'
      });
    }

    // Check cycle phase change (if using tracker)
    const cyclePhase = getCyclePhaseNotification();
    if (cyclePhase.showNotification && !isNotificationDismissed('cyclePhase') && !wasRecentlyRead('cyclePhase')) {
      notifications.push({
        id: notificationId++,
        type: 'cycle',
        title: `Entering ${cyclePhase.phase} Phase 🌸`,
        message: cyclePhase.tip,
        time: 'Today',
        read: false,
        action: { text: 'View Tracker', link: '/cycle-tracker' },
        priority: 'medium'
      });
    }

    // Check for new resources (added in last 7 days)
    const newResource = getNewResourceAlert();
    if (newResource.hasNew && !isNotificationDismissed('newResource') && !wasRecentlyRead('newResource')) {
      notifications.push({
        id: notificationId++,
        type: 'resource',
        title: `New Resource: ${newResource.title} 📚`,
        message: 'Fresh educational content just for you!',
        time: 'New',
        read: false,
        action: { text: 'Read Now', link: '/resources' },
        priority: 'low'
      });
    }

    // Check recipe suggestion (haven't used favorites in 2+ weeks, hide for 7 days after read)
    const recipeSuggestion = getRecipeSuggestion();
    if (recipeSuggestion.suggest && !isNotificationDismissed('recipeSuggestion') && !wasRecentlyRead('recipeSuggestion', 168)) {
      notifications.push({
        id: notificationId++,
        type: 'recipe',
        title: `Try cooking "${recipeSuggestion.recipeName}" ⭐`,
        message: `It's been a while since you used your favorites!`,
        time: 'Suggestion',
        read: false,
        action: { text: 'View Recipe', link: '/recipes' },
        priority: 'low'
      });
    }

    // First workout celebration
    const firstWorkout = getFirstWorkoutCelebration();
    if (firstWorkout.celebrate && !isNotificationDismissed('firstWorkout')) {
      notifications.push({
        id: notificationId++,
        type: 'milestone',
        title: `You completed your first workout! 🏆`,
        message: 'Welcome to the Alera fitness family!',
        time: 'Now',
        read: false,
        action: { text: 'Keep Going', link: '/workouts' },
        priority: 'high'
      });
    }

    // Sort by priority and LIMIT TO MAX 3 NOTIFICATIONS
    const priorityOrder = { high: 1, medium: 2, low: 3 };
    notifications.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  } catch (error) {
    console.error('Error generating notifications:', error);
  }

  // Return only top 3 most important notifications
  return notifications.slice(0, 3);
};

// Helper functions

function getLastWeighInDate() {
  try {
    // Check new MyProgress lastWeightEntry first
    const lastEntry = localStorage.getItem('lastWeightEntry');
    if (lastEntry) return parseInt(lastEntry);
    
    // Fallback to old weightTracking for backwards compatibility
    const weightEntries = JSON.parse(localStorage.getItem('weightTracking') || '[]');
    if (weightEntries.length === 0) return null;
    const sorted = weightEntries.sort((a, b) => new Date(b.date) - new Date(a.date));
    return new Date(sorted[0].date).getTime();
  } catch {
    return null;
  }
}

function getLastReflectionDate() {
  try {
    const lastReflection = localStorage.getItem('lastReflectionDate');
    return lastReflection ? parseInt(lastReflection) : null;
  } catch {
    return null;
  }
}

function getLastForumVisit() {
  try {
    const lastVisit = localStorage.getItem('lastForumVisit');
    return lastVisit ? parseInt(lastVisit) : Date.now();
  } catch {
    return Date.now();
  }
}

function getWorkoutStreak() {
  try {
    // Read from workoutHistory which is where WorkoutLibrary saves completed workouts
    const workoutHistory = JSON.parse(localStorage.getItem('workoutHistory') || '{}');
    
    // Extract all completedAt timestamps from workout history entries
    const completedDates = Object.values(workoutHistory)
      .filter(entry => entry.completedAt)
      .map(entry => new Date(entry.completedAt).toDateString());
    
    if (completedDates.length === 0) return 0;
    
    // Get unique dates sorted newest first
    const uniqueDates = [...new Set(completedDates)].sort((a, b) => new Date(b) - new Date(a));
    
    let streak = 0;
    let currentDate = new Date();
    
    for (let date of uniqueDates) {
      const workoutDate = new Date(date);
      const diffDays = Math.floor((currentDate - workoutDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays === streak) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  } catch {
    return 0;
  }
}

function getLastWorkoutDate() {
  try {
    const workoutHistory = JSON.parse(localStorage.getItem('workoutHistory') || '{}');
    
    // Find most recent completedAt timestamp
    const timestamps = Object.values(workoutHistory)
      .filter(entry => entry.completedAt)
      .map(entry => entry.completedAt);
    
    if (timestamps.length === 0) return null;
    
    return Math.max(...timestamps);
  } catch {
    return null;
  }
}

function getMealPlanStatus() {
  try {
    const lastPlanDate = localStorage.getItem('lastMealPlanDate');
    
    // If user has never planned meals, don't nag them — default to 0 (no reminder)
    const daysSinceLastPlan = lastPlanDate 
      ? Math.floor((Date.now() - parseInt(lastPlanDate)) / (1000 * 60 * 60 * 24))
      : 0;
    
    return { daysSinceLastPlan };
  } catch {
    return { daysSinceLastPlan: 0 };
  }
}

function getGoalProgress() {
  try {
    // Goals are stored in goalsV2.detailedGoals
    const goalsData = JSON.parse(localStorage.getItem('goalsV2') || '{}');
    const goals = goalsData.detailedGoals || [];
    if (goals.length === 0) return { hasGoals: false };
    
    // Find goals near milestones (90%+)
    for (let goal of goals) {
      if (goal.progress >= 90 && goal.progress < 100) {
        return {
          hasGoals: true,
          nearingMilestone: true,
          percentComplete: goal.progress,
          message: `Just ${100 - goal.progress}% more to complete "${goal.title}"!`
        };
      }
    }
    
    return { hasGoals: true, nearingMilestone: false };
  } catch {
    return { hasGoals: false };
  }
}

// New program alert - checks if there's a program created in last 7 days user hasn't seen
function getNewProgramAlert() {
  try {
    const lastSeenPrograms = JSON.parse(localStorage.getItem('seenProgramIds') || '[]');
    const programs = JSON.parse(localStorage.getItem('publicPrograms') || '{"programs":[]}');
    const programList = programs.programs || [];
    
    // Find programs created in last 7 days that haven't been seen
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    for (let program of programList) {
      const createdAt = program.created_at ? new Date(program.created_at).getTime() : 0;
      if (createdAt > sevenDaysAgo && !lastSeenPrograms.includes(program.id)) {
        return { hasNew: true, programTitle: program.title || 'New Program' };
      }
    }
    return { hasNew: false };
  } catch {
    return { hasNew: false };
  }
}

// Admin forum post - checks if Kate posted something new
function getAdminForumPost() {
  try {
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (isAdmin) return { hasNewPost: false }; // Don't notify admin of their own posts
    
    const adminPost = JSON.parse(localStorage.getItem('latestAdminPost') || 'null');
    if (!adminPost) return { hasNewPost: false };
    
    const lastSeenAdminPost = localStorage.getItem('lastSeenAdminPost');
    const postTimestamp = adminPost.timestamp;
    
    // If user hasn't seen this admin post yet
    if (!lastSeenAdminPost || parseInt(lastSeenAdminPost) < postTimestamp) {
      return {
        hasNewPost: true,
        preview: adminPost.title,
        category: adminPost.category
      };
    }
    
    return { hasNewPost: false };
  } catch {
    return { hasNewPost: false };
  }
}

// Forum replies - checks for new replies to user's posts
function getForumReplies() {
  try {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) return { hasReplies: false };
    
    const unreadReplies = JSON.parse(localStorage.getItem('unreadForumReplies') || '[]');
    if (unreadReplies.length === 0) return { hasReplies: false };
    
    return {
      hasReplies: true,
      replyCount: unreadReplies.length,
      latestReply: unreadReplies[0]?.preview || 'Someone replied to your post'
    };
  } catch {
    return { hasReplies: false };
  }
}

// Workout milestones - checks if user hit a milestone (10, 25, 50, 100)
function getWorkoutMilestone() {
  try {
    const workoutHistory = JSON.parse(localStorage.getItem('workoutHistory') || '{}');
    const completedCount = Object.values(workoutHistory).filter(w => w.completed || w.completedAt).length;
    const celebratedMilestones = JSON.parse(localStorage.getItem('celebratedMilestones') || '[]');
    
    const milestones = [10, 25, 50, 100, 150, 200];
    const messages = {
      10: "You're building a great habit!",
      25: "Quarter century of workouts! Amazing!",
      50: "Halfway to 100! Keep crushing it!",
      100: "Triple digits! You're unstoppable!",
      150: "150 workouts! True dedication!",
      200: "200 workouts! You're a legend!"
    };
    
    for (let m of milestones) {
      if (completedCount >= m && !celebratedMilestones.includes(m)) {
        return { hitMilestone: true, count: m, message: messages[m] };
      }
    }
    return { hitMilestone: false };
  } catch {
    return { hitMilestone: false };
  }
}

// Cycle phase notification - checks if entering a new phase today
function getCyclePhaseNotification() {
  try {
    const cycleData = JSON.parse(localStorage.getItem('cycleData') || 'null');
    if (!cycleData || !cycleData.lastPeriod) return { showNotification: false };
    
    const lastNotifiedPhase = localStorage.getItem('lastNotifiedCyclePhase');
    const lastPeriod = new Date(cycleData.lastPeriod);
    const today = new Date();
    const daysSincePeriod = Math.floor((today - lastPeriod) / (1000 * 60 * 60 * 24));
    const cycleLength = cycleData.avgCycleLength || 28;
    
    // Determine current phase
    let phase, tip;
    if (daysSincePeriod <= 5) {
      phase = 'Menstrual';
      tip = 'Focus on rest and gentle movement today.';
    } else if (daysSincePeriod <= 13) {
      phase = 'Follicular';
      tip = 'Great time for new challenges and strength training!';
    } else if (daysSincePeriod <= 16) {
      phase = 'Ovulation';
      tip = 'Peak energy! Perfect for high-intensity workouts.';
    } else if (daysSincePeriod <= cycleLength) {
      phase = 'Luteal';
      tip = 'Listen to your body - mix strength with recovery.';
    } else {
      return { showNotification: false };
    }
    
    // Only notify if phase changed
    if (lastNotifiedPhase === phase) return { showNotification: false };
    
    return { showNotification: true, phase, tip };
  } catch {
    return { showNotification: false };
  }
}

// New resource alert - checks for resources added in last 7 days
function getNewResourceAlert() {
  try {
    const lastSeenResources = JSON.parse(localStorage.getItem('seenResourceIds') || '[]');
    const resources = JSON.parse(localStorage.getItem('publicResources') || '[]');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    for (let resource of resources) {
      const createdAt = resource.created_at ? new Date(resource.created_at).getTime() : 0;
      if (createdAt > sevenDaysAgo && !lastSeenResources.includes(resource.id)) {
        return { hasNew: true, title: resource.title || 'New Resource' };
      }
    }
    return { hasNew: false };
  } catch {
    return { hasNew: false };
  }
}

// Recipe suggestion - suggests a favorite recipe if not cooked in 2+ weeks
function getRecipeSuggestion() {
  try {
    const favorites = JSON.parse(localStorage.getItem('recipeFavorites') || '[]');
    if (favorites.length === 0) return { suggest: false };
    
    const lastCookedDate = localStorage.getItem('lastCookedFromFavorites');
    if (!lastCookedDate) {
      // Never cooked from favorites - suggest one
      const randomFav = favorites[Math.floor(Math.random() * favorites.length)];
      return { suggest: true, recipeName: randomFav.title || 'a saved recipe' };
    }
    
    const daysSinceCooked = (Date.now() - parseInt(lastCookedDate)) / (1000 * 60 * 60 * 24);
    if (daysSinceCooked >= 14) {
      const randomFav = favorites[Math.floor(Math.random() * favorites.length)];
      return { suggest: true, recipeName: randomFav.title || 'a saved recipe' };
    }
    
    return { suggest: false };
  } catch {
    return { suggest: false };
  }
}

// First workout celebration - checks if just completed first ever workout
function getFirstWorkoutCelebration() {
  try {
    const workoutHistory = JSON.parse(localStorage.getItem('workoutHistory') || '{}');
    const completedCount = Object.values(workoutHistory).filter(w => w.completed || w.completedAt).length;
    const celebrated = localStorage.getItem('celebratedFirstWorkout');
    
    if (completedCount === 1 && !celebrated) {
      return { celebrate: true };
    }
    return { celebrate: false };
  } catch {
    return { celebrate: false };
  }
}

// Save forum visit timestamp
export const updateForumVisit = () => {
  try {
    localStorage.setItem('lastForumVisit', Date.now().toString());
    // Also mark any admin posts as seen
    const adminPost = JSON.parse(localStorage.getItem('latestAdminPost') || 'null');
    if (adminPost && adminPost.timestamp) {
      localStorage.setItem('lastSeenAdminPost', adminPost.timestamp.toString());
    }
  } catch (error) {
    console.error('Failed to update forum visit:', error);
  }
};

// Save meal plan timestamp
export const updateMealPlanDate = () => {
  try {
    localStorage.setItem('lastMealPlanDate', Date.now().toString());
  } catch (error) {
    console.error('Failed to update meal plan date:', error);
  }
};

// Save reflection timestamp
export const updateReflectionDate = () => {
  try {
    localStorage.setItem('lastReflectionDate', Date.now().toString());
  } catch (error) {
    console.error('Failed to update reflection date:', error);
  }
};

// Mark a milestone as celebrated
export const celebrateMilestone = (milestoneCount) => {
  try {
    const celebrated = JSON.parse(localStorage.getItem('celebratedMilestones') || '[]');
    if (!celebrated.includes(milestoneCount)) {
      celebrated.push(milestoneCount);
      localStorage.setItem('celebratedMilestones', JSON.stringify(celebrated));
    }
  } catch (error) {
    console.error('Failed to celebrate milestone:', error);
  }
};

// Mark first workout as celebrated
export const celebrateFirstWorkout = () => {
  try {
    localStorage.setItem('celebratedFirstWorkout', 'true');
  } catch (error) {
    console.error('Failed to celebrate first workout:', error);
  }
};

// Update cycle phase notification tracking
export const updateCyclePhaseNotified = (phase) => {
  try {
    localStorage.setItem('lastNotifiedCyclePhase', phase);
  } catch (error) {
    console.error('Failed to update cycle phase notification:', error);
  }
};

// Mark a program as seen (for new program alerts)
export const markProgramAsSeen = (programId) => {
  try {
    const seen = JSON.parse(localStorage.getItem('seenProgramIds') || '[]');
    if (!seen.includes(programId)) {
      seen.push(programId);
      localStorage.setItem('seenProgramIds', JSON.stringify(seen));
    }
  } catch (error) {
    console.error('Failed to mark program as seen:', error);
  }
};

// Mark a resource as seen (for new resource alerts)
export const markResourceAsSeen = (resourceId) => {
  try {
    const seen = JSON.parse(localStorage.getItem('seenResourceIds') || '[]');
    if (!seen.includes(resourceId)) {
      seen.push(resourceId);
      localStorage.setItem('seenResourceIds', JSON.stringify(seen));
    }
  } catch (error) {
    console.error('Failed to mark resource as seen:', error);
  }
};

// Update last cooked from favorites timestamp
export const updateLastCookedFromFavorites = () => {
  try {
    localStorage.setItem('lastCookedFromFavorites', Date.now().toString());
  } catch (error) {
    console.error('Failed to update last cooked date:', error);
  }
};

// Add unread forum reply (called when someone replies to user's post)
export const addUnreadForumReply = (replyPreview) => {
  try {
    const replies = JSON.parse(localStorage.getItem('unreadForumReplies') || '[]');
    replies.unshift({ preview: replyPreview, timestamp: Date.now() });
    localStorage.setItem('unreadForumReplies', JSON.stringify(replies.slice(0, 10))); // Keep max 10
  } catch (error) {
    console.error('Failed to add forum reply:', error);
  }
};

// Clear unread forum replies (when user views their posts)
export const clearUnreadForumReplies = () => {
  try {
    localStorage.setItem('unreadForumReplies', '[]');
  } catch (error) {
    console.error('Failed to clear forum replies:', error);
  }
};
